import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Upload, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/lib/content";

const empty = { name: "", role: "", photo: "", order: 0 };

const PhotoPicker = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Фото загружено");
    } catch {
      toast.error("Ошибка загрузки");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="size-16 rounded-lg overflow-hidden bg-muted border border-border shrink-0">
        {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
      </div>
      <input ref={ref} type="file" hidden accept="image/*" onChange={upload} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted disabled:opacity-50"
      >
        <Upload className="size-3.5" />
        {busy ? "..." : value ? "Заменить" : "Загрузить"}
      </button>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="или вставьте URL"
        className="flex-1 h-9 px-3 rounded-lg border border-input bg-background text-xs"
      />
    </div>
  );
};

const MemberRow = ({ m, onSave, onDelete }) => {
  const [form, setForm] = useState(m);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setForm(m); setDirty(false); }, [m]);
  const update = (patch) => { setForm({ ...form, ...patch }); setDirty(true); };
  return (
    <div className="p-4 rounded-2xl border border-border bg-card space-y-3" data-testid={`team-row-${m.id}`}>
      <PhotoPicker value={form.photo} onChange={(v) => update({ photo: v })} />
      <div className="grid sm:grid-cols-3 gap-2">
        <input value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Имя" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid={`team-name-${m.id}`} />
        <input value={form.role || ""} onChange={(e) => update({ role: e.target.value })} placeholder="Должность" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" />
        <input type="number" value={form.order} onChange={(e) => update({ order: parseInt(e.target.value || "0") })} placeholder="Порядок" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={() => onDelete(m)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-destructive/40 text-destructive text-xs font-medium hover:bg-destructive hover:text-destructive-foreground transition-colors" data-testid={`team-delete-${m.id}`}>
          <Trash2 className="size-3.5" /> Удалить
        </button>
        <button
          onClick={() => onSave(form)}
          disabled={!dirty}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-foreground text-background text-xs font-semibold disabled:opacity-40"
          data-testid={`team-save-${m.id}`}
        >
          <Save className="size-3.5" /> Сохранить
        </button>
      </div>
    </div>
  );
};

export default function AdminTeam() {
  const { refetch } = useContent();
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newM, setNewM] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/team");
    setMembers(data);
  };

  useEffect(() => { load(); }, []);

  const save = async (m) => {
    try {
      await api.put(`/team/${m.id}`, m);
      toast.success("Сохранено");
      await load(); refetch();
    } catch { toast.error("Ошибка сохранения"); }
  };

  const del = async (m) => {
    if (!window.confirm(`Удалить ${m.name}?`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Удалено");
      await load(); refetch();
    } catch { toast.error("Не удалось удалить"); }
  };

  const create = async () => {
    if (!newM.name.trim()) { toast.error("Введите имя"); return; }
    try {
      await api.post("/team", { ...newM, order: newM.order || members.length });
      toast.success("Сотрудник добавлен");
      setAdding(false); setNewM(empty); await load(); refetch();
    } catch { toast.error("Ошибка"); }
  };

  return (
    <div className="max-w-5xl w-full">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Команда</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Сотрудники команды</h1>
          <p className="text-sm text-muted-foreground mt-2">{members.length} сотрудников</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-foreground text-background font-semibold text-sm"
          data-testid="team-add-btn"
        >
          <Plus className="size-4" /> Добавить сотрудника
        </button>
      </div>

      {adding && (
        <div className="p-4 rounded-2xl border border-accent/40 bg-card space-y-3 mb-6 glow-accent" data-testid="team-add-form">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold">New member</h3>
            <button onClick={() => { setAdding(false); setNewM(empty); }} className="size-8 rounded-full hover:bg-muted flex items-center justify-center">
              <X className="size-4" />
            </button>
          </div>
          <PhotoPicker value={newM.photo} onChange={(v) => setNewM({ ...newM, photo: v })} />
          <div className="grid sm:grid-cols-3 gap-2">
            <input value={newM.name} onChange={(e) => setNewM({ ...newM, name: e.target.value })} placeholder="Имя" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="team-new-name" />
            <input value={newM.role} onChange={(e) => setNewM({ ...newM, role: e.target.value })} placeholder="Должность" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" />
            <input type="number" value={newM.order} onChange={(e) => setNewM({ ...newM, order: parseInt(e.target.value || "0") })} placeholder="Порядок" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" />
          </div>
          <div className="flex justify-end">
            <button onClick={create} className="h-9 px-4 rounded-lg bg-foreground text-background text-xs font-semibold" data-testid="team-create-submit">Создать</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {members.map((m) => <MemberRow key={m.id} m={m} onSave={save} onDelete={del} />)}
      </div>
    </div>
  );
}
